import { useState } from 'react'

export default function ContactForm({ subject = 'Generel henvendelse', dark = false }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    project_address: '',
    postal_code: '',
    message: '',
    website_url: '', // Honeypot field
  })
  const [status, setStatus] = useState('idle') // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('')

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('submitting')
    setErrorMessage('')

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          subject,
        }),
      })

      if (response.ok) {
        setStatus('success')
        setFormData({
          name: '',
          email: '',
          phone: '',
          project_address: '',
          postal_code: '',
          message: '',
          website_url: '',
        })
      } else {
        const errData = await response.json().catch(() => ({}))
        throw new Error(errData.message || 'Der skete en fejl ved afsendelsen. Prøv venligst igen.')
      }
    } catch (err) {
      setStatus('error')
      setErrorMessage(err.message || 'Der kunne ikke oprettes forbindelse til serveren. Tjek din internetforbindelse.')
    }
  }

  const containerClasses = dark
    ? "bg-[#222222]/40 backdrop-blur-md rounded-none text-white"
    : "bg-transparent rounded-none shadow-none text-slate-900"

  const labelClasses = dark
    ? "block text-xs font-bold uppercase tracking-wider text-slate-300 mb-1"
    : "block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1"

  const inputClasses = dark
    ? "w-full px-4 py-3 rounded-none bg-[#111111] border border-[#333333] text-white focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-all text-sm"
    : "w-full px-4 py-3 rounded-none bg-white border border-slate-300 text-slate-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-all text-sm"

  return (
    <div className={containerClasses}>
      {status === 'success' ? (
        <div className={`border p-8 rounded-none text-center ${dark ? 'bg-[#111111] border-emerald-500/30 text-emerald-400' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
          <i className="fa-solid fa-circle-check text-5xl text-emerald-500 mb-4 animate-bounce"></i>
          <h4 className="text-xl font-bold mb-2">Tak for din besked!</h4>
          <p className="text-sm">Vi har modtaget din henvendelse og vender tilbage til dig så hurtigt som muligt.</p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-6 text-xs font-bold uppercase tracking-wider text-brand-orange hover:text-brand-orange-hover transition-colors"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6 bg-transparent">
          {/* Honeypot field - absolutely hidden from humans */}
          <div className="sr-only absolute -z-10" aria-hidden="true">
            <label htmlFor="website_url">Website URL</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div>
            <label htmlFor="name" className={labelClasses}>
              Navn <span className="text-brand-orange">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className={inputClasses}
              placeholder="Dit fulde navn"
            />
          </div>

          <div>
            <label htmlFor="email" className={labelClasses}>
              E-mail <span className="text-brand-orange">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className={inputClasses}
              placeholder="din@email.dk"
            />
          </div>

          <div>
            <label htmlFor="phone" className={labelClasses}>
              Telefonnummer
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={inputClasses}
              placeholder="+45 1234 5678"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label htmlFor="project_address" className={labelClasses}>
                Projektadresse (Hvor opgaven er)
              </label>
              <input
                type="text"
                id="project_address"
                name="project_address"
                value={formData.project_address}
                onChange={handleChange}
                className={inputClasses}
                placeholder="Vejnavn og husnr."
              />
            </div>
            <div>
              <label htmlFor="postal_code" className={labelClasses}>
                Postnr. &amp; by
              </label>
              <input
                type="text"
                id="postal_code"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                className={inputClasses}
                placeholder="F.eks. 8250 Egå"
              />
            </div>
          </div>

          <div>
            <label htmlFor="message" className={labelClasses}>
              Besked <span className="text-brand-orange">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows="5"
              value={formData.message}
              onChange={handleChange}
              className={inputClasses}
              placeholder="Beskriv din opgave her..."
            ></textarea>
          </div>

          {status === 'error' && (
            <div className={`border p-4 rounded-none text-sm flex items-start gap-3 ${dark ? 'bg-[#111111] border-rose-500/30 text-rose-400' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
              <i className="fa-solid fa-circle-exclamation mt-0.5 text-rose-500"></i>
              <span>{errorMessage}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full bg-brand-orange hover:bg-brand-orange-hover text-white font-bold py-4 px-8 uppercase text-xs tracking-wider rounded-none transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {status === 'submitting' ? (
              <>
                <i className="fa-solid fa-spinner animate-spin"></i>
                Sender...
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane"></i>
                Send besked
              </>
            )}
          </button>
        </form>
      )}
    </div>
  )
}
